import { deckOfTheDayIds } from 'features/battle/deck';
import { BATTLE_DECK_SIZE } from 'features/battle/types';
import { BATTLE_BADGE_IDS } from 'features/catalog/badge-definitions';

export const CUSTOM_BATTLE_DECK = 'custom' as const;
export const DECK_OF_THE_DAY = 'deck-of-the-day' as const;

type SampleBattleDeckShape = {
  id: string;
  name: string;
  blurb: string;
  badgeIds: readonly string[];
};

/**
 * Shared 12-badge lists anyone can play. Top-end curves are limited to
 * 1–3 Legendaries, or 1 Mythic with 1–2 Legendaries.
 * Each deck uses Legendaries and Mythics no other sample deck uses, where possible.
 */
export const SAMPLE_BATTLE_DECKS = [
  {
    id: 'last-place-pays',
    name: 'Last Place Pays',
    blurb: 'SAMPLE DECK: Fall behind. On purpose. Heroically.',
    badgeIds: [
      'srt3', // SRT Paladin — while behind, gains 2 and loses 1 Spotlight
      'ibdm', // Talos Olympics Winner 24' — while behind, gains 3 and discards itself

      'jt0d', // Champion — steals 1 Prestige while tied
      'c9da', // Split The G — both draw; if only yours has Spotlight, reveal and activate it
      'st3p', // Step by Step — an unbroken row of 3 scores 2, then returns to hand
      '3tgz', // I'm Always Down — discards the right pile, or steals 1 Prestige if it is empty

      'fyzv', // Volunteer — gains 1 while behind
      'rtdk', // Office Olympian — a full board of Commons and Rares scores 2
      '52nh', // Pet Parade Participant — reveals Fun and scores
      'l1ys', // Rolling 6s — Commons reveal, Rares score
      'cupp', // World Cup Sweepstake Participant — draws, and a correct rarity guess scores
      'ifu1', // Guinness Master — both draw, and a matching rarity scores
    ],
  },
  {
    id: 'ship-it-twice',
    name: 'Ship It Twice',
    blurb: 'SAMPLE DECK: Ship it once, ship it twice, never write a postmortem.',
    badgeIds: [
      '0gfr', // Galactic Speaker — while tied, gains 2 and boosts Growth

      'vib2', // Vibe Legend — activates an Engineering draw, otherwise discards it and gains 1
      'n26f', // Presenter Prodigy — with 2 Rares revealed, gains 1 and discards non-Rares
      'wg99', // Beef Wellington Bandit — non-Common draws score
      'dz9q', // Honorary Viking — draws; reveals Fun, otherwise discards it and gains 1
      'st3p', // Step by Step — an unbroken row of 3 scores 2, then returns to hand
      '3tgz', // I'm Always Down — discards the right pile, or steals 1 Prestige if it is empty
      '21yr', // Explorer — swaps, and scores 2 when both new neighbors are occupied

      'p12z', // Gitcraft Wizard — gains 1 after another Engineering reveal or activation this turn
      'dr56', // Hackathon Participant — reveals and activates an Engineering badge from hand
      'htci', // Buzz Builder — gains 1
      'mh96', // Tech Trailblazer — while behind, gains 1, draws 2, and reveals Spotlight
    ],
  },
  {
    id: 'bagel-cashout',
    name: 'Bagel Cashout',
    blurb: 'SAMPLE DECK: Bring cream cheese.',
    badgeIds: [
      'uwpa', // Badge Baron — if the board shares a stat, gains 2 and discards a Culture
      'slg4', // Interview Icon — draws 2, then reveals and activates one Common
      'b4uv', // Swag Hunter — gains 2 Prestige, then discards itself
      '21yr', // Explorer — swaps onto their board and gains 2
      'setl', // Always Settle — returns another badge to gain 1 Prestige
      'm0v3', // On the Move — draws, then this badge permanently gains +2 in a chosen stat
      '65ry', // Badger — scores with more board badges than the opponent

      't61t', // Fika Feast — both draw; a Culture draw scores
      'fyzv', // Volunteer — while behind, gains 1
      'gm0p', // Soho Social — both draw, then a Culture draw is revealed
      'seed', // Seed Planter — gains 3 while you have 2 or fewer Prestige
      'ziyg', // Everything Bagel — with a Legendary revealed, gains 4 and discards the board
    ],
  },
  {
    id: 'house-edge',
    name: 'House Edge',
    blurb: 'SAMPLE DECK: The house always wins. Today, you are the house.',
    badgeIds: [
      'wcup', // World Cup Sweepstake Winner — draws 2, and each correct rarity guess scores
      'rac1', // Talos Racing Champion — draws 2, reveals Fun, and activates it while ahead
      'ibdm', // Talos Olympics Winner 24' — while behind, gains 3 and discards itself

      'rt3p', // Poker Shark — the higher discarded rarity scores 2
      'jt0d', // Champion — steals 1 Prestige while tied
      'pq4t', // In Debt! — discards 2 and draws 3

      'cupp', // World Cup Sweepstake Participant — draws, and a correct rarity guess scores
      'wg99', // Beef Wellington Bandit — non-Common draws score
      'ifu1', // Guinness Master — both draw, and a matching rarity scores
      '52nh', // Pet Parade Participant — reveals Fun and scores
      'rtdk', // Office Olympian — a full board of Commons and Rares scores 2
      'l1ys', // Rolling 6s — Commons reveal, Rares score
    ],
  },
  {
    id: 'pixel-raid',
    name: 'Pixel Raid',
    blurb: "SAMPLE DECK: Don't stand in fire. Do stand in their empty pile.",
    badgeIds: [
      '20nu', // Pixel Prestige — with every board badge holding Spotlight or Fun, steals 2 and discards itself
      '5tgz', // Serial Packer — gains 1 per empty unlocked opponent pile

      'wg99', // Beef Wellington Bandit — non-Common draws score
      '1mew', // Jira Enthusiast — draws 2, reveals Engineering, and bottoms one
      '65ry', // Badger — scores with more board badges than the opponent
      'tpbo', // Foundational Forest — with the left pile empty and unlocked, draws and gains 1
      'dz9q', // Honorary Viking — draws; reveals Fun, otherwise discards it and gains 1
      'olof', // Olof Approved — scores when the drawn rarity is already on your board
      'bfo1', // Big Foot Sighting — a correct rarity guess discards their badge and scores
      '3tgz', // I'm Always Down — discards the right pile, or steals 1 Prestige if it is empty

      'y0az', // Tunesmith — exactly 3 Fun badges on board score and boost Spotlight
      '52nh', // Pet Parade Participant — reveals Fun and scores
    ],
  },
  {
    id: 'even-odds',
    name: 'Even Odds',
    blurb: 'SAMPLE DECK: Tie first. Cash second.',
    badgeIds: [
      'deal', // International Deal Conqueror — while tied, gains 2 and reveals from hand

      'wg99', // Beef Wellington Bandit — non-Common draws score
      'jt0d', // Champion — steals 1 Prestige while tied
      'snp0', // All for One — with 2+ in hand, steals 1 Prestige and discards 2
      '21yr', // Explorer — swaps, and scores 2 when both new neighbors are occupied
      'st3p', // Step by Step — an unbroken row of 3 scores 2, then returns to hand
      'perf', // Performance Ninja — another Spotlight activation this turn scores 2

      'srt2', // SRT Knight — gains 1 Prestige and loses 1 Spotlight
      'seed', // Seed Planter — gains 3 while you have 2 or fewer Prestige
      '52nh', // Pet Parade Participant — reveals Fun and scores
      'htci', // Buzz Builder — gains 1
      '3tgz', // I'm Always Down — discards the right pile, or steals 1 Prestige if it is empty
    ],
  },
  {
    id: 'jet-set-cashout',
    name: 'Jet Getaway',
    blurb: 'SAMPLE DECK: Fire the thrusters, take 3 actions, and cash every pile.',
    badgeIds: [
      'bxbq', // Referral Royalty — draws 3 and gains 1 Prestige per Growth drawn
      'hpai', // Jet Boots — Ongoing: all effects can activate, regardless of stat lead
      'nogh', // Timekeeper of Truth — on your second action, gains 2, draws 2, and discards itself

      'bbtw', // Talos Takedown — Ongoing: Legendary badges cannot be revealed
      'tk16', // Culture Catalyst — On Reveal: take 3 Actions on your next turn
      'srt2', // SRT Knight — gains 1 Prestige and loses 1 Spotlight
      '3tgz', // I'm Always Down — steals 1 Prestige, then discards itself
      '21yr', // Explorer — swaps with a badge on their board and gains 2

      'mx26', // Riviera Maya 2026 — Ongoing: neighboring badges gain +1 Prestige when activated
      '8nll', // Battle Hardened! — steals 1 Prestige and skips an action on your next turn
      'b4uv', // Swag Hunter — gains 2 Prestige, then discards itself
      'htci', // Buzz Builder — gains 1
    ],
  },
] as const satisfies readonly SampleBattleDeckShape[];

export type SampleBattleDeckId = (typeof SAMPLE_BATTLE_DECKS)[number]['id'];

export type SampleBattleDeck = {
  id: SampleBattleDeckId | typeof DECK_OF_THE_DAY;
  /** Archetype plus how the deck tries to reach 10 Prestige. */
  name: string;
  blurb: string;
  badgeIds: readonly string[];
};

export type BattleDeckSource = typeof CUSTOM_BATTLE_DECK | typeof DECK_OF_THE_DAY | SampleBattleDeckId;

const deckOfTheDayList = (date = new Date()): SampleBattleDeck => ({
  id: DECK_OF_THE_DAY,
  name: 'Deck of the Day',
  blurb: `DECK OF THE DAY - 12 badges for ${date.toLocaleDateString()}, at most 2 Premium Stats.`,
  badgeIds: deckOfTheDayIds(BATTLE_BADGE_IDS, date),
});

export const sampleBattleDeckById = (id: string, date = new Date()): SampleBattleDeck | null => {
  if (id === DECK_OF_THE_DAY) return deckOfTheDayList(date);
  return SAMPLE_BATTLE_DECKS.find((deck) => deck.id === id) ?? null;
};

/** The CPU always plays one of the sample decks, chosen by seed. */
export const cpuBattleDeck = (seed: number): string[] => {
  const index = Math.abs(Math.trunc(seed || 0)) % SAMPLE_BATTLE_DECKS.length;
  return [...SAMPLE_BATTLE_DECKS[index].badgeIds];
};

export const isSampleBattleDeckSource = (source: string): source is SampleBattleDeckId | typeof DECK_OF_THE_DAY =>
  sampleBattleDeckById(source) != null;

const sameBadgeIds = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) return false;
  const other = new Set(b);
  return other.size === a.length && a.every((id) => other.has(id));
};

/** The named sample list whose badges match `ids` 1-1, or null. Deck of the Day is not a named list. */
export const sampleBattleDeckForIds = (ids: readonly string[]): (typeof SAMPLE_BATTLE_DECKS)[number] | null => {
  if (ids.length !== BATTLE_DECK_SIZE) return null;
  return SAMPLE_BATTLE_DECKS.find((sample) => sameBadgeIds(sample.badgeIds, ids)) ?? null;
};

/** True when `ids` is exactly one of the shared sample lists, including Deck of the Day. */
export const matchesSampleBattleDeck = (ids: readonly string[], date = new Date()): boolean => {
  if (ids.length !== BATTLE_DECK_SIZE) return false;
  if (sampleBattleDeckForIds(ids)) return true;
  if (sameBadgeIds(deckOfTheDayIds(BATTLE_BADGE_IDS, date), ids)) return true;
  const yesterday = new Date(date);
  yesterday.setDate(yesterday.getDate() - 1);
  return sameBadgeIds(deckOfTheDayIds(BATTLE_BADGE_IDS, yesterday), ids);
};
